// react
import { useCallback, useState } from 'react';
import styled from 'styled-components';

// components
import { Title } from '@/components/atoms/Title/Title';
import { Button, Dialog, Option } from '@/components/atoms';
import { BackButton, CareCircle } from '@/components/molecules';
import { CareCircleInvite } from '@/components/organisms';

// models
import {
  CareCircle as CareCircleType,
  CareCircleMember,
  CareCircleMemberInvite,
  Message,
} from '@karehero/models';

/**
 * CareCircleMemberAll is the page for the Care Circle.
 */
export const CareCircleMemberAll = ({
  careCircle,
  onPreview,
  onInvite,
  relationshipOptions,
}: CareCircleMemberAllProps) => {
  // state
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  // methods
  const handleInvite = useCallback(
    async (careCircleMemberInvite: CareCircleMemberInvite) => {
      try {
        const res = await onInvite(careCircleMemberInvite);
        if (!res || 'error' in res) return;
        setIsInviteOpen(false);
      } catch (e) {
        console.error(e);
      }
    },
    [onInvite],
  );

  return (
    <>
      <Wrapper>
        <BackButton />
        <Header>
          <Title>Care Circle</Title>
        </Header>
        <Text>
          Boost your support network by inviting friends and family to your
          KareHero Care Circle, giving them access to resources and direct
          connection with your Care Expert.
        </Text>
        <CareCircle
          onAdd={() => setIsInviteOpen(true)}
          onPreview={onPreview}
          careRecipientMember={{
            id: careCircle?.careRecipientAccount?.id || 'id',
            account: {
              ...careCircle?.careRecipientAccount,
            },
            fallback: `${
              careCircle?.careRecipientAccount.firstName?.[0] || ''
            }`,
          }}
          members={
            careCircle?.careCircleMembers
              ?.filter(
                (member) =>
                  member.account.id !== careCircle?.careRecipientAccount.id,
              )
              .map((member) => ({
                id: member.id || 'id',
                account: {
                  ...member.account,
                },
                name:
                  `${member.account.firstName} ${member.account.lastName}` ||
                  'name',
                roles: member.roles || [],
                fallback: `${member?.account?.firstName?.[0]}`,
              })) || []
          }
        />
      </Wrapper>
      <Dialog isOpen={isInviteOpen} setIsOpen={setIsInviteOpen}>
        <CareCircleInvite
          careCircle={careCircle}
          onInvite={handleInvite}
          onCancel={() => setIsInviteOpen(false)}
          relationshipOptions={relationshipOptions}
        />
      </Dialog>
    </>
  );
};

const Wrapper = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[24]};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Text = styled.div`
  font: ${(props) => props.theme.typography.body.sm.medium};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: left;
  width: 100%;
  margin-bottom: ${(props) => props.theme.spacing[24]};
`;

interface CareCircleMemberAllProps {
  careCircle?: CareCircleType;
  onPreview: (member: CareCircleMember) => void;
  onInvite: (
    careCircleMemberInvite: CareCircleMemberInvite,
  ) => Promise<Message | undefined>;
  relationshipOptions: Option[];
}

CareCircleMemberAll.displayName = 'CareCircleMemberAll';

export default CareCircleMemberAll;
